'use client';

import { useState } from 'react';
import Image from 'next/image';
import { X } from 'lucide-react';

export interface GalleryItem {
  title: string;
  category: string;
  description: string;
  image: string;
}

interface GalleryContentLabels {
  open: string;
  image_alt: string;
  full_size: string;
  close: string;
}

export default function GalleryContent({
  items,
  labels,
}: {
  items: GalleryItem[];
  labels: GalleryContentLabels;
}) {
  const [selectedImage, setSelectedImage] = useState<GalleryItem | null>(null);

  return (
    <>
      {/* Gallery Grid */}
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {items.map((example, index) => (
          <button
            key={`${example.title}-${index}`}
            type="button"
            className="group bg-card hover:border-primary/50 flex cursor-pointer flex-col overflow-hidden rounded-lg border transition-all hover:shadow-lg"
            onClick={() => setSelectedImage(example)}
            aria-label={`${labels.open} ${example.title}`}
          >
            {/* Image Display */}
            <div className="flex aspect-square items-center justify-center overflow-hidden bg-black">
              <Image
                src={example.image}
                alt={labels.image_alt.replace('{title}', example.title)}
                width={320}
                height={320}
                className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, (max-width: 1280px) 33vw, 25vw"
                quality={60}
                unoptimized
              />
            </div>

            {/* Info */}
            <div className="shrink-0 border-t p-3">
              <h3 className="font-medium">{example.title}</h3>
              <p className="text-muted-foreground text-sm">
                {example.category}
              </p>
            </div>
          </button>
        ))}
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button
            className="absolute top-4 right-4 rounded-full bg-white/10 p-2 transition-colors hover:bg-white/20"
            onClick={() => setSelectedImage(null)}
            aria-label={labels.close}
          >
            <X className="h-6 w-6 text-white" />
          </button>

          <div
            className="relative flex max-h-[90vh] max-w-5xl flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <Image
              src={selectedImage.image}
              alt={labels.full_size.replace('{title}', selectedImage.title)}
              width={1200}
              height={1200}
              className="max-h-[80vh] max-w-full rounded-lg object-contain"
              sizes="90vw"
              quality={75}
              unoptimized
            />
            <div className="mt-4 text-center text-white">
              <h3 className="text-xl font-semibold">{selectedImage.title}</h3>
              <p className="mt-1 text-white/70">{selectedImage.description}</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
